import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/constants";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Users, Search, Star, Briefcase, MapPin, DollarSign, Clock, Award, Send, Building, BarChart3, Settings } from "lucide-react";

const AvailBadge = ({ status }) => {
  const c = { available: "bg-green-100 text-green-700", limited: "bg-yellow-100 text-yellow-700", unavailable: "bg-red-100 text-red-700" };
  return <span className={`px-2 py-0.5 rounded text-xs font-medium ${c[status] || "bg-gray-100"}`}>{status}</span>;
};

export default function ExpertWitnessPage() {
  const [tab, setTab] = useState("browse");
  const [experts, setExperts] = useState([]);
  const [specialties, setSpecialties] = useState({});
  const [stats, setStats] = useState(null);
  const [selectedExpert, setSelectedExpert] = useState(null);
  const [filters, setFilters] = useState({ specialty: "", jurisdiction: "", case_type: "", max_rate: "" });
  const [engageForm, setEngageForm] = useState({ case_name: "", case_type: "", engagement_type: "consultation", jurisdiction: "", case_summary: "", requestor_name: "", requestor_email: "", requestor_firm: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    axios.get(`${API}/expert-witness/experts`).then(r => setExperts(r.data.experts || []));
    axios.get(`${API}/expert-witness/specialties`).then(r => setSpecialties(r.data.categories || {}));
    axios.get(`${API}/expert-witness/dashboard/stats`).then(r => setStats(r.data));
  }, []);

  const searchExperts = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.specialty) params.specialty = filters.specialty;
      if (filters.jurisdiction) params.jurisdiction = filters.jurisdiction;
      if (filters.case_type) params.case_type = filters.case_type;
      if (filters.max_rate) params.max_rate = parseFloat(filters.max_rate);
      const { data } = await axios.get(`${API}/expert-witness/experts`, { params });
      setExperts(data.experts || []);
    } finally { setLoading(false); }
  };

  const submitEngagement = async () => {
    if (!selectedExpert || !engageForm.case_name || !engageForm.requestor_name || !engageForm.requestor_email) return;
    try {
      await axios.post(`${API}/expert-witness/engagements`, { ...engageForm, expert_id: selectedExpert.id });
      alert("Engagement request submitted successfully!");
      setSelectedExpert(null);
      setEngageForm({ case_name: "", case_type: "", engagement_type: "consultation", jurisdiction: "", case_summary: "", requestor_name: "", requestor_email: "", requestor_firm: "" });
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to submit");
    }
  };

  return (
    <div className="space-y-6" data-testid="expert-witness-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Expert Witness Network</h1>
        <p className="text-slate-500 mt-1">Connect with verified court experts and testimonial specialists</p>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Expert Witnesses", value: stats.total_experts, icon: Users, color: "text-blue-600" },
            { label: "Specialties", value: stats.total_specialties, icon: Award, color: "text-purple-600" },
            { label: "Engagements", value: stats.total_engagements, icon: Briefcase, color: "text-green-600" },
            { label: "Avg Rate/Hr", value: `$${stats.avg_expert_rate}`, icon: DollarSign, color: "text-amber-600" },
          ].map(s => (
            <Card key={s.label}>
              <CardContent className="py-3 flex items-center gap-3">
                <s.icon className={`w-8 h-8 ${s.color}`} />
                <div><div className="text-lg font-bold">{s.value}</div><div className="text-xs text-slate-500">{s.label}</div></div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="browse" data-testid="tab-browse-experts">Browse Experts</TabsTrigger>
          <TabsTrigger value="specialties" data-testid="tab-specialties">Specialties</TabsTrigger>
          <TabsTrigger value="engage" data-testid="tab-engage">Engage Expert</TabsTrigger>
          <TabsTrigger value="whitelabel" data-testid="tab-whitelabel">White Label</TabsTrigger>
        </TabsList>

        <TabsContent value="browse" className="mt-4 space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Specialty</Label>
                  <Select value={filters.specialty || "all"} onValueChange={val => setFilters(f => ({...f, specialty: val === "all" ? "" : val}))}>
                    <SelectTrigger data-testid="filter-specialty-select" className="h-9">
                      <SelectValue placeholder="All Specialties" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Specialties</SelectItem>
                      {Object.values(specialties).flat().map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs">Jurisdiction</Label><Input className="h-9" data-testid="filter-jurisdiction" value={filters.jurisdiction} onChange={e => setFilters(f => ({...f, jurisdiction: e.target.value}))} placeholder="e.g. California" /></div>
                <div><Label className="text-xs">Max Rate/Hr</Label><Input className="h-9" type="number" data-testid="filter-max-rate" value={filters.max_rate} onChange={e => setFilters(f => ({...f, max_rate: e.target.value}))} placeholder="$" /></div>
                <div className="flex items-end"><Button onClick={searchExperts} disabled={loading} className="w-full h-9" data-testid="search-experts-btn"><Search className="w-4 h-4 mr-1" />Search</Button></div>
              </div>
            </CardContent>
          </Card>

          <div className="space-y-3">
            {experts.map(expert => (
              <Card key={expert.id} className="hover:shadow-md transition-shadow" data-testid={`expert-card-${expert.id}`}>
                <CardContent className="py-4">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-lg">{expert.name}</h3>
                        <AvailBadge status={expert.availability} />
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5">{expert.credentials}</p>
                      <div className="flex flex-wrap gap-3 mt-2 text-xs text-slate-600">
                        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{expert.years_experience} yrs exp</span>
                        <span className="flex items-center gap-1"><Award className="w-3 h-3" />{expert.total_testimonies} testimonies</span>
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{expert.location}</span>
                        <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" />${expert.rate_per_hour}/hr</span>
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2">
                        {expert.sub_specialties?.map(s => <span key={s} className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs">{s}</span>)}
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" data-testid={`engage-expert-${expert.id}`} onClick={() => { setSelectedExpert(expert); setTab("engage"); }}>
                        <Send className="w-3 h-3 mr-1" />Engage
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="specialties" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(specialties).map(([cat, specs]) => (
              <Card key={cat}>
                <CardHeader className="pb-2"><CardTitle className="text-base">{cat}</CardTitle></CardHeader>
                <CardContent>
                  <ul className="space-y-1.5">
                    {specs.map(s => (
                      <li key={s.id} className="text-sm text-slate-600 flex items-center gap-2 cursor-pointer hover:text-blue-600"
                        onClick={() => { setFilters(f => ({...f, specialty: s.id})); setTab("browse"); searchExperts(); }}>
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />{s.name}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="engage" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                Request Expert Engagement
                {selectedExpert && <span className="text-sm font-normal text-slate-500 ml-2">for {selectedExpert.name}</span>}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!selectedExpert && <p className="text-sm text-slate-500">Select an expert from the Browse tab first, or fill in the form to submit a general request.</p>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Case Name *</Label><Input data-testid="engage-case-name" value={engageForm.case_name} onChange={e => setEngageForm(f => ({...f, case_name: e.target.value}))} placeholder="Smith v. Jones" /></div>
                <div><Label>Case Type</Label><Input data-testid="engage-case-type" value={engageForm.case_type} onChange={e => setEngageForm(f => ({...f, case_type: e.target.value}))} placeholder="Personal Injury, Fraud..." /></div>
                <div>
                  <Label>Engagement Type</Label>
                  <Select value={engageForm.engagement_type} onValueChange={val => setEngageForm(f => ({...f, engagement_type: val}))}>
                    <SelectTrigger data-testid="engagement-type-select" className="h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="consultation">Consultation</SelectItem>
                      <SelectItem value="report">Written Report</SelectItem>
                      <SelectItem value="deposition">Deposition</SelectItem>
                      <SelectItem value="trial">Trial Testimony</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Jurisdiction</Label><Input data-testid="engage-jurisdiction" value={engageForm.jurisdiction} onChange={e => setEngageForm(f => ({...f, jurisdiction: e.target.value}))} placeholder="California" /></div>
                <div><Label>Your Name *</Label><Input data-testid="engage-requestor-name" value={engageForm.requestor_name} onChange={e => setEngageForm(f => ({...f, requestor_name: e.target.value}))} placeholder="Attorney name" /></div>
                <div><Label>Your Email *</Label><Input data-testid="engage-requestor-email" value={engageForm.requestor_email} onChange={e => setEngageForm(f => ({...f, requestor_email: e.target.value}))} placeholder="attorney@firm.com" /></div>
                <div className="md:col-span-2"><Label>Your Firm</Label><Input data-testid="engage-requestor-firm" value={engageForm.requestor_firm} onChange={e => setEngageForm(f => ({...f, requestor_firm: e.target.value}))} placeholder="Law firm name" /></div>
                <div className="md:col-span-2"><Label>Case Summary</Label><textarea className="w-full min-h-[80px] px-3 py-2 rounded-md border text-sm" data-testid="engage-case-summary" value={engageForm.case_summary} onChange={e => setEngageForm(f => ({...f, case_summary: e.target.value}))} placeholder="Brief description of the case and what you need the expert for..." /></div>
              </div>
              <Button data-testid="submit-engagement-btn" onClick={submitEngagement} className="bg-blue-600 hover:bg-blue-700 text-white" disabled={!engageForm.case_name || !engageForm.requestor_name || !engageForm.requestor_email}>
                <Send className="w-4 h-4 mr-2" />Submit Engagement Request
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="whitelabel" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Settings className="w-5 h-5 text-blue-600" />White Label Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-slate-500">Customize the Expert Witness Platform with your branding for resale to law firms, insurance companies, and investigation agencies.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>Platform Name</Label><Input defaultValue="Trialeze Expert Network" placeholder="Your Brand Name" /></div>
                <div><Label>Contact Email</Label><Input defaultValue="experts@trialeze.com" placeholder="contact@yourbrand.com" /></div>
                <div><Label>Primary Color</Label><Input type="color" defaultValue="#1D4ED8" /></div>
                <div><Label>Fee Markup %</Label><Input type="number" defaultValue="15" placeholder="Revenue markup on expert fees" /></div>
                <div className="md:col-span-2"><Label>Custom Domain</Label><Input placeholder="experts.yourdomain.com" /></div>
              </div>
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-800 mb-2">Revenue Model</h4>
                <ul className="text-sm text-blue-700 space-y-1">
                  <li>15% markup on all expert engagement fees</li>
                  <li>$99/month per white-label tenant</li>
                  <li>Volume discounts for 10+ tenants</li>
                  <li>Custom branding, domain, and email templates</li>
                </ul>
              </div>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-whitelabel-btn"><Building className="w-4 h-4 mr-2" />Save White Label Config</Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
